/**
 * Looking at part of a big island through a small window.
 *
 * The builder used to size its canvas to the whole island multiplied by the zoom, and let the
 * surrounding element scroll. That is the obvious design and it has two failures, one of which had
 * already shipped:
 *
 *   - **The canvas can be too big to allocate.** The backing store was `island × zoom × devicePixelRatio`.
 *     VEŽBALIŠTE is 53×43 tiles, so at the default 2× on a 3× phone that is a 5088×4128 canvas —
 *     21 megapixels, about 84 MB — and at 4× it is 84 megapixels. Past what iOS Safari will hand
 *     out, and a refused allocation does not throw: it renders blank.
 *   - **It could not be panned by a finger at all.** `.wb-canvas` carries `touch-action: none` (it
 *     has to, or a stroke turns into a page scroll and pointermove stops arriving), and the canvas
 *     filled its scroller — so on a tablet there was no gesture left that could reach the far side
 *     of a large island.
 *
 * So the canvas is now the size of the *window*, with a hard cap on its area, and a camera moves
 * over the art buffer underneath it. That is the design `game/engine.ts` has always used, and it is
 * worth copying rather than inventing: the 1:1 art buffer is untouched, which means
 * `renderGroundLayer`, the ground cache and the whole overlay pass carry on exactly as they were and
 * the only new code is the final blit.
 *
 * Everything here is pure and integer. `viewTransform` is computed once per frame and used by both
 * the blit and the hit-test, because a viewport whose drawing and whose pointer maths are derived
 * separately is a viewport that paints one tile away from the cursor.
 */

/**
 * The most pixels a builder canvas may allocate.
 *
 * Sixteen megapixels is comfortably more than any viewport needs (a 2560×1440 window at dpr 2 is
 * 14.7) and comfortably under what a phone will refuse. The cap is applied by dropping the device
 * pixel ratio rather than the viewport size, because a slightly soft canvas beats a blank one.
 */
export const MAX_BACKING_PX = 16_000_000;

export interface Camera {
  x: number;
  y: number;
}

export interface ViewTransform {
  /** Top-left of the visible region of the art buffer, in buffer pixels. Always an integer. */
  camX: number;
  camY: number;
  /** How much of the buffer is visible, in buffer pixels. */
  sw: number;
  sh: number;
  /**
   * Where the blit lands inside the window, in CSS pixels. Non-zero only when the island is smaller
   * than the window, in which case it is centred — the alternative is a small island wedged into the
   * top-left corner with grey to the right of it.
   */
  offsetX: number;
  offsetY: number;
  /** Whole-number magnification. Never fractional: `image-rendering: pixelated` at 2.5× gives tile
   *  edges five device pixels wide next to ones six wide, which is visible on every tile. */
  zoom: number;
}

/** Everything about the view except where it is pointed. */
export interface ViewGeometry {
  /** The art buffer, in pixels: island tiles × TILE. */
  pixelW: number;
  pixelH: number;
  /** The window, in CSS pixels. */
  viewW: number;
  viewH: number;
  zoom: number;
}

export interface ViewInput extends ViewGeometry {
  camera: Camera;
}

const clamp = (value: number, low: number, high: number): number =>
  Math.max(low, Math.min(high, value));

/** How far the camera may travel before the far edge of the island would leave the window. */
export function cameraBounds(input: ViewInput): { maxX: number; maxY: number } {
  const visibleW = Math.ceil(input.viewW / input.zoom);
  const visibleH = Math.ceil(input.viewH / input.zoom);
  return {
    maxX: Math.max(0, input.pixelW - visibleW),
    maxY: Math.max(0, input.pixelH - visibleH),
  };
}

export function clampCamera(input: ViewInput): Camera {
  const bounds = cameraBounds(input);
  return {
    x: Math.round(clamp(input.camera.x, 0, bounds.maxX)),
    y: Math.round(clamp(input.camera.y, 0, bounds.maxY)),
  };
}

export function viewTransform(input: ViewInput): ViewTransform {
  const camera = clampCamera(input);
  const sw = Math.min(input.pixelW, Math.ceil(input.viewW / input.zoom));
  const sh = Math.min(input.pixelH, Math.ceil(input.viewH / input.zoom));
  return {
    camX: camera.x,
    camY: camera.y,
    sw,
    sh,
    offsetX: Math.max(0, Math.round((input.viewW - sw * input.zoom) / 2)),
    offsetY: Math.max(0, Math.round((input.viewH - sh * input.zoom) / 2)),
    zoom: input.zoom,
  };
}

/**
 * The device pixel ratio to actually use, after the area cap.
 *
 * Stepped down a whole integer at a time and never below 1, so the result is still a whole-number
 * scale — see `ViewTransform.zoom`.
 */
export function backingScale(viewW: number, viewH: number, dpr: number, cap = MAX_BACKING_PX): number {
  let scale = Math.max(1, Math.min(3, Math.round(dpr) || 1));
  while (scale > 1 && viewW * scale * viewH * scale > cap) scale -= 1;
  return scale;
}

/**
 * Which tile a pointer is over, or null when it is outside the island.
 *
 * `local` is the pointer relative to the window's top-left, in CSS pixels — i.e.
 * `clientX - rect.left`. Null rather than a clamp: a stroke that starts in the margin beside a small
 * centred island should not paint its edge column.
 */
export function tileAtPointer(
  transform: ViewTransform,
  local: { x: number; y: number },
  tileSize: number,
  width: number,
  height: number,
): { x: number; y: number } | null {
  const bufferX = (local.x - transform.offsetX) / transform.zoom + transform.camX;
  const bufferY = (local.y - transform.offsetY) / transform.zoom + transform.camY;
  const x = Math.floor(bufferX / tileSize);
  const y = Math.floor(bufferY / tileSize);
  if (x < 0 || y < 0 || x >= width || y >= height) return null;
  return { x, y };
}

/** Which point of the art buffer is under a window point, in fractional buffer pixels. */
export function bufferPointAt(
  transform: ViewTransform,
  local: { x: number; y: number },
): { x: number; y: number } {
  return {
    x: (local.x - transform.offsetX) / transform.zoom + transform.camX,
    y: (local.y - transform.offsetY) / transform.zoom + transform.camY,
  };
}

/**
 * The camera that puts a given point of the buffer under a given point of the window.
 *
 * This is the whole of anchored zooming and of pinch-to-zoom, and it is one function rather than two
 * because the alternative is two nearly-identical bits of arithmetic that drift.
 */
export function cameraPutting(
  input: ViewGeometry,
  bufferPoint: { x: number; y: number },
  local: { x: number; y: number },
): Camera {
  const placed = viewTransform({ ...input, camera: { x: 0, y: 0 } });
  return clampCamera({
    ...input,
    camera: {
      x: bufferPoint.x - (local.x - placed.offsetX) / input.zoom,
      y: bufferPoint.y - (local.y - placed.offsetY) / input.zoom,
    },
  });
}

/**
 * Zoom while keeping one point of the island under the same place on screen.
 *
 * Without this the view walks towards the top-left every time somebody zooms in, because the camera
 * stays where it was while the magnification around it grows. `focus` is in CSS pixels within the
 * window; the centre is the right default for a button press, and a pinch passes its own midpoint.
 */
export function zoomAround(input: ViewInput, nextZoom: number, focus?: { x: number; y: number }): Camera {
  const point = focus ?? { x: input.viewW / 2, y: input.viewH / 2 };
  const held = bufferPointAt(viewTransform(input), point);
  return cameraPutting({ ...input, zoom: nextZoom }, held, point);
}

export const ZOOM_MIN = 1;
export const ZOOM_MAX = 6;

/** One step of zoom, bounded. Kept here so the buttons and a pinch cannot disagree about the range. */
export const stepZoom = (zoom: number, direction: -1 | 1): number =>
  clamp(Math.round(zoom) + direction, ZOOM_MIN, ZOOM_MAX);
